/**
 * Gestionnaire de prefetch intelligent pour les ressources critiques
 */

interface PrefetchOptions {
  priority?: 'high' | 'low';
  as?: 'script' | 'style' | 'image' | 'fetch';
}

class PrefetchManager {
  private prefetchedUrls = new Set<string>();
  private observer: IntersectionObserver | null = null;

  constructor() {
    this.initIntersectionObserver();
  }

  /**
   * Initialise l'IntersectionObserver pour le prefetch au survol
   */
  private initIntersectionObserver() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLAnchorElement;
            const href = target.getAttribute('href');
            
            if (href && !this.prefetchedUrls.has(href)) {
              this.prefetchRoute(href);
            }
          }
        });
      },
      { rootMargin: '50px' }
    );
  }

  /**
   * Prefetch une route
   */
  prefetchRoute(url: string) {
    if (this.prefetchedUrls.has(url)) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    
    document.head.appendChild(link);
    this.prefetchedUrls.add(url);
  }

  /**
   * Prefetch une ressource (image, script, style)
   */
  prefetchResource(url: string, options: PrefetchOptions = {}) {
    if (this.prefetchedUrls.has(url)) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    
    if (options.as) {
      link.as = options.as;
    }
    
    if (options.priority === 'high') {
      link.setAttribute('importance', 'high');
    }
    
    document.head.appendChild(link);
    this.prefetchedUrls.add(url);
  }

  /**
   * Preload une ressource critique
   */
  preloadResource(url: string, options: PrefetchOptions = {}) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    
    if (options.as) {
      link.as = options.as;
    }
    
    document.head.appendChild(link);
  }

  /**
   * Observe un élément pour le prefetch au survol
   */
  observeLink(element: HTMLElement) {
    if (this.observer) {
      this.observer.observe(element);
    }
  }

  /**
   * Arrête d'observer un élément
   */
  unobserveLink(element: HTMLElement) {
    if (this.observer) {
      this.observer.unobserve(element);
    }
  }

  /**
   * Prefetch les routes critiques au chargement
   */
  prefetchCriticalRoutes() {
    const criticalRoutes = [
      '/pets',
      '/health',
      '/profile',
      '/calendar'
    ];

    // Délai pour ne pas bloquer le chargement initial
    setTimeout(() => {
      criticalRoutes.forEach(route => this.prefetchRoute(route));
    }, 2000);
  }

  /**
   * Prefetch les images visibles dans le viewport
   */
  prefetchVisibleImages() {
    if (!this.observer) return;

    const images = document.querySelectorAll('img[data-prefetch]');
    images.forEach(img => {
      if (img instanceof HTMLElement) {
        this.observeLink(img);
      }
    });
  }

  /**
   * Nettoie les prefetch
   */
  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.prefetchedUrls.clear();
  }
}

export const prefetchManager = new PrefetchManager();

/**
 * Hook pour prefetch au survol d'un lien
 */
export function usePrefetchOnHover(href: string) {
  const handleMouseEnter = () => {
    prefetchManager.prefetchRoute(href);
  };

  return { onMouseEnter: handleMouseEnter };
}

/**
 * Initialise le prefetch global
 */
export function initGlobalPrefetch() {
  if (typeof window === 'undefined') return;

  // Prefetch des routes critiques après le chargement
  if (document.readyState === 'complete') {
    prefetchManager.prefetchCriticalRoutes();
  } else {
    window.addEventListener('load', () => {
      prefetchManager.prefetchCriticalRoutes();
    });
  }

  // Prefetch des images au scroll
  prefetchManager.prefetchVisibleImages();
}
